interface TelegramThemeParams {
  bg_color?: string
  text_color?: string
  hint_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  secondary_bg_color?: string
  section_bg_color?: string
  section_separator_color?: string
  destructive_text_color?: string
}

interface TelegramSafeAreaInset {
  top: number
  bottom: number
  left: number
  right: number
}

interface TelegramBackButton {
  show(): void
  hide(): void
  onClick(callback: () => void): void
  offClick(callback: () => void): void
}

interface TelegramWebApp {
  initData: string
  colorScheme: 'light' | 'dark'
  themeParams: TelegramThemeParams
  safeAreaInset?: TelegramSafeAreaInset
  contentSafeAreaInset?: TelegramSafeAreaInset
  BackButton: TelegramBackButton
  ready(): void
  expand(): void
  onEvent(event: 'themeChanged' | 'safeAreaChanged' | 'contentSafeAreaChanged', callback: () => void): void
  offEvent(event: 'themeChanged' | 'safeAreaChanged' | 'contentSafeAreaChanged', callback: () => void): void
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export interface TelegramMiniApp {
  readonly initData: string
  setBackHandler(handler: (() => void) | null): void
  destroy(): void
}

let activeMiniApp: TelegramMiniApp | null = null

export function isTelegramMiniAppPath(pathname = window.location.pathname): boolean {
  return /^\/tg(?:\/|$)/iu.test(pathname)
}

// Архив всегда выглядит одинаково — светлым, как сайт. Тему Telegram
// сознательно не применяем: при тёмной теме клиента мини-апп расходился
// с сайтом по цветам, хотя это одна и та же страница.
const APP_BACKGROUND = '#f4f2ed'

function applyTheme(): void {
  document.documentElement.style.colorScheme = 'light'
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', APP_BACKGROUND)
}

function applySafeArea(webApp: TelegramWebApp): void {
  const safe = webApp.safeAreaInset
  const content = webApp.contentSafeAreaInset
  const root = document.documentElement
  for (const side of ['top', 'right', 'bottom', 'left'] as const) {
    root.style.setProperty(
      `--tg-app-safe-${side}`,
      `${Math.max(safe?.[side] ?? 0, content?.[side] ?? 0)}px`,
    )
  }
}

export function initializeTelegramMiniApp(): TelegramMiniApp | null {
  if (activeMiniApp || !isTelegramMiniAppPath()) return activeMiniApp
  document.documentElement.classList.add('telegram-mini-app')
  const webApp = window.Telegram?.WebApp
  if (!webApp) return null

  webApp.ready()
  webApp.expand()
  applyTheme()
  applySafeArea(webApp)

  const onThemeChanged = () => applyTheme()
  const onSafeAreaChanged = () => applySafeArea(webApp)
  webApp.onEvent('themeChanged', onThemeChanged)
  webApp.onEvent('safeAreaChanged', onSafeAreaChanged)
  webApp.onEvent('contentSafeAreaChanged', onSafeAreaChanged)

  let backHandler: (() => void) | null = null
  activeMiniApp = {
    initData: webApp.initData,
    setBackHandler(handler) {
      if (backHandler) webApp.BackButton.offClick(backHandler)
      backHandler = handler
      if (handler) {
        webApp.BackButton.onClick(handler)
        webApp.BackButton.show()
      } else {
        webApp.BackButton.hide()
      }
    },
    destroy() {
      if (backHandler) webApp.BackButton.offClick(backHandler)
      webApp.BackButton.hide()
      webApp.offEvent('themeChanged', onThemeChanged)
      webApp.offEvent('safeAreaChanged', onSafeAreaChanged)
      webApp.offEvent('contentSafeAreaChanged', onSafeAreaChanged)
      activeMiniApp = null
    },
  }
  return activeMiniApp
}

export function getTelegramMiniApp(): TelegramMiniApp | null {
  return activeMiniApp
}
